/**
 * Admin · Workspace defaults (super-admin, spec 019 Wave-2 T4) — edit the PLATFORM-GLOBAL
 * output defaults (format / quality / width-height / max-canvas / presets) stored on the
 * NULL-tenant settings row. These are the fall-through beneath each tenant's own settings and
 * every device's local overrides (resolution: local → tenant → global → hardcoded). Written
 * via `updateSettings(..., undefined, "global")`; the server 403s a non-super-admin. Saving
 * pushes the fresh value into the shared settings cache so re-warming surfaces inherit it.
 */
import { useCallback, useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import { ActionBar } from "../ui/ActionBar.js";
import { InfoHint } from "../ui/Tooltip.js";
import { cacheEpoch, readCache, writeCache } from "../stores/cache.js";
import { CACHE_KEYS } from "../stores/sessionWarmup.js";
// WS-B B-10 / MIN-11: the five output fields + the preset row are ONE component now.
import { OutputDefaultsFields, OutputPresetButtons } from "../settings/OutputDefaultsFields.js";
import { HARDCODED_OUTPUT_DEFAULTS } from "../settings/localSettings.js";
import type { ResolvedWorkspaceSettings, WorkspaceOutputSettings } from "@genz/platform";
import { RowAction } from "../ui/RowAction.js";


/**
 * P1: an explicitly EMPTIED "Max canvas" must clear the stored ceiling. The old code
 * omitted the key entirely when null, so the value could never be unset once written.
 * All three legs are now aligned: `UpdateSettingsInput.max_canvas_px` is `number | null`,
 * and `PUT /api/settings` uses pydantic's `model_fields_set` to tell an explicit null
 * (clear → inherit the platform default) from an absent key (leave unchanged).
 */

/** The platform-global output defaults from a settings response (else hardcoded fallbacks). */
function fromSettings(s: ResolvedWorkspaceSettings | null): WorkspaceOutputSettings {
  return { ...HARDCODED_OUTPUT_DEFAULTS, ...(s?.global_defaults ?? {}) };
}

export function WorkspaceDefaultsTab() {
  const { client } = useServices();
  const [d, setD] = useState<WorkspaceOutputSettings>(
    () => fromSettings(readCache<ResolvedWorkspaceSettings>(CACHE_KEYS.settings)),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const epoch = cacheEpoch();
    try {
      const fresh = await client.getSettings();
      writeCache(CACHE_KEYS.settings, fresh, epoch);
      setD(fromSettings(fresh));
    } catch (e) {
      if (readCache<ResolvedWorkspaceSettings>(CACHE_KEYS.settings) == null) {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  }, [client]);

  useEffect(() => { void load(); }, [load]);

  function set<K extends keyof WorkspaceOutputSettings>(key: K, value: WorkspaceOutputSettings[K]) {
    setD((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const updated = await client.updateSettings({
        output_width: d.output_width, output_height: d.output_height,
        output_format: d.output_format, output_quality: d.output_quality,
        max_canvas_px: d.max_canvas_px ?? null, // explicit null = clear the ceiling
        output_presets: d.output_presets,
      }, undefined, "global");
      // P1: do NOT write a scope=global response into the TENANT-scoped settings cache.
      // The response is the NULL-tenant row — including its FTP fields — and the tenant
      // Settings panel seeds itself from this key, so saving platform defaults used to
      // show the global row's FTP config as if it were the tenant's own.
      setD(fromSettings(updated));
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    /* Owner Rule B — the admin Workspace defaults page is NOT the customer Settings page.
       This node used to carry BOTH vocabularies (`admin-tab settings`) and four more of
       Settings' class names inside, from a stylesheet only `settings/SettingsPanel.tsx`
       imports: the admin page was being styled by a sheet it never asks for. It speaks
       admin's vocabulary now, declared in `admin/admin.css`. The FIELDS still come from the
       shared `OutputDefaultsFields` — sharing the data layer is allowed; sharing the page's
       chrome is what Rule B forbids. */
    <section className="admin-tab" data-testid="admin-workspace-defaults">
      <div className="admin-panel">
        <div className="admin-panel__head admin-panel__head--flush">
          <strong className="admin-panel__title panel-title">
            Workspace output defaults{" "}
            <InfoHint text="Platform-wide fallback for output format, quality, size, max canvas and presets. Every tenant inherits these unless it (or a specific device) overrides them." />
          </strong>
        </div>
        {error && <p role="alert" data-testid="wd-error">{error}</p>}

        {/* WS-B B-10 / MIN-11: the SAME five fields the customer Settings page renders, from
            one component. No `meta` — a platform default has nothing above it to inherit
            from — and no blanking of Max canvas, because here the number IS the ceiling. */}
        <OutputDefaultsFields
          values={d}
          onChange={(k, v) => set(k as keyof WorkspaceOutputSettings, v as never)}
          testIdPrefix="wd"
        />

        <div className="admin-presets" data-testid="wd-presets">
          <OutputPresetButtons
            presets={d.output_presets}
            onAdd={(next) => set("output_presets", next)}
          />
        </div>
        {d.output_presets.length > 0 && (
          <ul className="admin-preset-list" data-testid="wd-preset-list">
            {d.output_presets.map((p, i) => (
              <li key={`${p.name}-${i}`} data-testid={`wd-preset-${i}`}>
                <span className="admin-preset-list__name">{p.name}</span>
                <span className="faint">{p.width}×{p.height} · {p.format} · q{p.quality}</span>
                <RowAction
                  label={`Delete preset ${p.name}`}
                  icon="trash"
                  tone="danger"
                  data-testid={`wd-preset-delete-${i}`}
                  onClick={() => set("output_presets", d.output_presets.filter((_, j) => j !== i))}
                />
              </li>
            ))}
          </ul>
        )}

        {/* Rule 7 — the page's commit, at the bottom of the column, in the shared bar.
            `.settings__actions` was one of the four local action rows the primitive replaces. */}
        <ActionBar
          label="Workspace default actions"
          primary={
            <button type="button" className="btn btn--primary" disabled={busy}
              data-testid="wd-save" onClick={() => void save()}>
              {busy ? "Saving…" : "Save"}
            </button>
          }
        >
          {saved && <span className="faint" data-testid="wd-saved">Saved · applied platform-wide.</span>}
        </ActionBar>
      </div>
    </section>
  );
}
